<template>
    <div class="product-prices-form">
        <v-tabs v-model="activeTab" height="30" class="mt-2 mb-6">
            <v-tab v-for="tab in tabs" :key="tab.name" :value="tab.name" class="text-caption text-primary">
                {{ tab.label }}
            </v-tab>
        </v-tabs>
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <locale-switcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-tabs-window v-model="activeTab">
            <v-tabs-window-item value="tab-tariffs">
                <product-prices-tariffs
                    :contract="contract"
                    :selected-locale="selectedLocale"
                    :price-lists="priceLists"
                    :is-price-lists-loading="isPriceListsLoading"
                    :price-lists-extras="priceListsExtras"
                    :is-price-lists-extras-loading="isPriceListsExtrasLoading"
                    :commissions="commissions"
                    :is-commissions-loading="isCommissionsLoading"
                    :penalties="penalties"
                    :is-penalties-loading="isPenaltiesLoading"
                    :discounts="discounts"
                    :is-discounts-loading="isDiscountsLoading"
                    :tariffs="tariffs"
                    :is-tariffs-loading="isTariffsLoading"
                    @on-tariff-save="loadTariffs"
                    @create-price-list="createPriceList"
                    @create-price-list-extra="createPriceListExtra"
                    @create-penalty="createPenalty"
                    @create-discount="createDiscount"
                />
            </v-tabs-window-item>
            <v-tabs-window-item value="tab-price-lists-extra-services">
                <product-prices-price-lists-for-extra-services
                    ref="productPricesPriceListsExtra"
                    :contract="contract"
                    :price-lists="priceListsExtras"
                    :is-price-lists-loading="isPriceListsExtrasLoading"
                    :selected-locale="selectedLocale"
                    @on-price-list-save="loadPriceListsForExtras"
                />
            </v-tabs-window-item>
            <v-tabs-window-item value="tab-discounts">
                <product-prices-discounts
                    ref="productPricesDiscounts"
                    :contract="contract"
                    :selected-locale="selectedLocale"
                    :discounts="discounts"
                    :is-discounts-loading="isDiscountsLoading"
                    @on-discount-save="loadDiscounts"
                />
            </v-tabs-window-item>
            <v-tabs-window-item value="tab-penalties">
                <product-prices-penalties
                    ref="productPricesPenalties"
                    :contract="contract"
                    :selected-locale="selectedLocale"
                    :penalties="penalties"
                    :is-penalties-loading="isPenaltiesLoading"
                    @on-penalty-save="loadPenalties"
                    @on-penalties-filter-change="onPenaltiesFilterChange"
                />
            </v-tabs-window-item>
            <v-tabs-window-item v-if="isSurchargesTabVisible" value="tab-surcharges">
                <product-prices-surcharges
                    ref="productPricesSurcharges"
                    :contract="contract"
                    :selected-locale="selectedLocale"
                    :surcharges="surcharges"
                    :is-surcharges-loading="isSurchargesLoading"
                    @on-surcharge-save="loadAllSurcharges"
                />
            </v-tabs-window-item>
        </v-tabs-window>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator'
    import ProductPricesTariffs from '~src/components/supplier/prices/ProductPricesTariffs.vue'
    import ProductPricesPriceLists from '~/components/supplier/prices/ProductPricesPriceLists.vue'
    import ProductPricesPriceListsForExtraServices from '~/components/supplier/prices/ProductPricesPriceListsForExtraServices.vue'
    import ProductPricesDiscounts from '~/components/supplier/prices/ProductPricesDiscounts.vue'
    import ProductPricesPenalties from '~/components/supplier/prices/ProductPricesPenalties.vue'
    import ProductPricesSurcharges from '~/components/supplier/prices/ProductPricesSurcharges.vue'
    import {contractServiceType} from '~/utils/types/types'
    import ProductPricesTariffsModal from '~src/components/supplier/prices/modals/ProductPricesTariffsModal.vue'

    @Component({
        components: {
            ProductPricesTariffsModal,
            ProductPricesTariffs,
            ProductPricesPriceLists,
            ProductPricesPriceListsForExtraServices,
            ProductPricesDiscounts,
            ProductPricesPenalties,
            ProductPricesSurcharges,
        },
    })
    export class ProductPricesForm extends Vue {
        @Prop() contract

        activeTab = null
        selectedLocale = null

        isPriceListsLoading = false
        priceLists = []
        isPriceListsExtrasLoading = false
        priceListsExtras = []
        isCommissionsLoading = false
        commissions = []
        isPenaltiesLoading = false
        penalties = []
        isDiscountsLoading = false
        discounts = []
        isTariffsLoading = false
        tariffs = []
        isSurchargesLoading = false
        surcharges = []

        @Watch('activeTab')
        async onTabChange() {
            if (this.activeTab === 'tab-tariffs') {
                await this.loadTariffs()
                await Promise.all([
                    this.loadPriceLists(),
                    this.loadPriceListsForExtras(),
                    this.loadCommissions(),
                    this.loadAllPenalties(),
                    this.loadDiscounts(),
                    ...(this.isSurchargesTabVisible ? [this.loadAllSurcharges()] : []),
                ])
            } else if (this.activeTab === 'tab-price-lists-extra-services') {
                await this.loadPriceListsForExtras()
            } else if (this.activeTab === 'tab-penalties') {
                await this.loadPenalties()
            } else if (this.activeTab === 'tab-discounts') {
                await this.loadDiscounts()
            } else if (this.activeTab === 'tab-surcharges') {
                await this.loadAllSurcharges()
            }
        }

        changeActiveLangCode(code) {
            this.selectedLocale = code
        }

        async createPriceListExtra() {
            this.activeTab = 'tab-price-lists-extra-services'
            await this.$nextTick
            if (this.$refs.productPricesPriceListsExtra?.$refs?.modeSwitcher) {
                this.$refs.productPricesPriceListsExtra.$refs.modeSwitcher.create()
            }
        }

        async createPenalty() {
            this.activeTab = 'tab-penalties'
            await this.$nextTick
            if (this.$refs.productPricesPenalties?.$refs?.modeSwitcher) {
                this.$refs.productPricesPenalties.$refs.modeSwitcher.create()
            }
        }

        async createDiscount() {
            this.activeTab = 'tab-discounts'
            await this.$nextTick
            if (this.$refs.productPricesDiscounts?.$refs?.modeSwitcher) {
                this.$refs.productPricesDiscounts.$refs.modeSwitcher.create()
            }
        }

        async loadTariffs() {
            try {
                this.isTariffsLoading = true
                this.tariffs = (
                    await this.$api.ownProductTariffs.get({
                        contractId: this.contract.id || this.$route.params?.contractId,
                    })
                ).tariffs
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isTariffsLoading = false
            }
        }

        async loadDiscounts() {
            try {
                this.isDiscountsLoading = true
                this.discounts = (
                    await this.$api.ownProductDiscounts.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                    })
                ).discounts
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isDiscountsLoading = false
            }
        }

        async loadCommissions() {
            try {
                this.isCommissionsLoading = true
                this.commissions = (
                    await this.$api.ownProductCommissions.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                    })
                ).commissions
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isCommissionsLoading = false
            }
        }

        async loadPriceLists() {
            try {
                this.isPriceListsLoading = true
                this.priceLists = (
                    await this.$api[this.priceListsMethod].get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                    })
                ).priceLists
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isPriceListsLoading = false
            }
        }

        get priceListsMethod() {
            switch (this.contract?.serviceType) {
                case contractServiceType.ownExtraService:
                    return 'ownExtraServicePriceLists'
                default:
                    return 'ownProductPriceLists'
            }
        }

        async loadPriceListsForExtras() {
            try {
                this.isPriceListsExtrasLoading = true
                this.priceListsExtras = (
                    await this.$api.ownProductPriceListExtras.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                    })
                ).priceListExtras
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isPriceListsExtrasLoading = false
            }
        }

        async loadPenalties(active = true) {
            try {
                this.isPenaltiesLoading = true
                this.penalties = (
                    await this.$api.ownProductPenalties.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                        active,
                    })
                ).penalties
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isPenaltiesLoading = false
            }
        }

        async loadAllPenalties() {
            try {
                this.isPenaltiesLoading = true
                const [activePenalties, inactivePenalties] = await Promise.all([
                    this.$api.ownProductPenalties.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                        active: true,
                    }),
                    this.$api.ownProductPenalties.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                        active: false,
                    }),
                ])
                this.penalties = [...activePenalties.penalties, ...inactivePenalties.penalties]
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isPenaltiesLoading = false
            }
        }

        onPenaltiesFilterChange() {
            const activeFilter = this.$refs.productPricesPenalties?.activeFilter
            if (activeFilter !== undefined) {
                this.loadPenalties(activeFilter)
            }
        }

        async loadAllSurcharges() {
            try {
                this.isSurchargesLoading = true
                const [activeRs, inactiveRs] = await Promise.all([
                    this.$api.ownProductPriceListSurcharges.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                        active: true,
                    }),
                    this.$api.ownProductPriceListSurcharges.get({
                        contractId: this.contract?.id || this.$route.params?.contractId,
                        active: false,
                    }),
                ])
                this.surcharges = [
                    ...(activeRs?.priceListSurcharges || []),
                    ...(inactiveRs?.priceListSurcharges || []),
                ]
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isSurchargesLoading = false
            }
        }

        get isSurchargesTabVisible() {
            return this.contract?.serviceType === contractServiceType.ownHotel
        }

        get tabs() {
            let tabs = [
                {
                    name: 'tab-tariffs',
                    label: this.$t('tariffs'),
                },
            ]

            if (this.contract?.serviceType !== contractServiceType.customComplexTour) {
                tabs = [
                    ...tabs,
                    {
                        name: 'tab-price-lists-extra-services',
                        label: this.$t('price_lists_extra_services'),
                    },
                ]
            }

            if (this.contract?.serviceType !== contractServiceType.ownVisa) {
                tabs = [
                    ...tabs,
                    {
                        name: 'tab-discounts',
                        label: this.$t('discounts'),
                    },
                ]
            }

            tabs = [
                ...tabs,
                {
                    name: 'tab-penalties',
                    label: this.$t('penalties'),
                },
            ]

            if (this.isSurchargesTabVisible) {
                tabs = [
                    ...tabs,
                    {
                        name: 'tab-surcharges',
                        label: this.$t('surcharges'),
                    },
                ]
            }

            return tabs
        }
    }

    export default ProductPricesForm
</script>
